/**
 * loanRemittanceReportModel.js — the Loan Remittance report's pure model
 * (PRD_LoanRemittanceReport C1/C4/C4a/C5/C5a).
 *
 * PURE: no jsPDF, no Firestore, no clock. Reads only what a loan document
 * carries — `name`, `employeeId`, `monthlyAmortization`, `totalPayable`,
 * `paidBefore` and the approve-only `paidByMonth` ledger (C6). Member numbers
 * are not read here: rows carry the employee's `docId` and the page reads PII
 * for those (C3), so the ambiguity rule (C4a) lives in this file only.
 */
import { round2 } from './contributionTableClient';
import { num } from '../utils/loanMath';

const collapseSpaces = (name) => String(name || '').trim().replace(/\s+/g, ' ');

/** C1 — the grouping key: trimmed, inner whitespace collapsed, lowercase. */
export const normalizeLoanName = (name) => collapseSpaces(name).toLowerCase();

const upperFirst = (a, b) => a.localeCompare(b, undefined, { caseFirst: 'upper' });

/**
 * C1 — one option per normalised name, A–Z. The label is the spelling (spaces
 * collapsed, case kept) used by the most loans in the group; a tie goes to the
 * spelling that sorts first with uppercase first (plain localeCompare puts
 * 'sss salary loan' first).
 * @param {Array<Object>} loans - non-voided loans (fetchCompanyLoans)
 * @returns {Array<{ key: string, label: string }>}
 */
export const loanNameOptions = (loans) => {
  const groups = new Map(); // key → Map(spelling → count)
  (loans || []).forEach((loan) => {
    const key = normalizeLoanName(loan?.name);
    if (!key) return;
    const spellings = groups.get(key) || new Map();
    const spelling = collapseSpaces(loan.name);
    spellings.set(spelling, (spellings.get(spelling) || 0) + 1);
    groups.set(key, spellings);
  });
  return [...groups.entries()]
    .map(([key, spellings]) => {
      const [label] = [...spellings.entries()].sort((a, b) => b[1] - a[1] || upperFirst(a[0], b[0]))[0];
      return { key, label };
    })
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
};

/**
 * C5a — what is left to deduct once every month up to and including
 * `monthKey` is credited. paidByMonth moves in lock-step with paidTotal on
 * both ledger writers, so this matches loanRemaining unless a later month
 * already holds a credit.
 */
export const remainingAtMonthEnd = (loan, monthKey) => {
  const credited = Object.entries(loan?.paidByMonth || {})
    .filter(([k]) => k <= monthKey)
    .reduce((sum, [, v]) => sum + num(v), 0);
  return Math.max(0, round2(num(loan?.totalPayable) - num(loan?.paidBefore) - credited));
};

/**
 * C4/C4a/C5 — one row per loan in the name group credited more than ₱0 in
 * `month`, employeeId ascending then loan id.
 *
 * A loan stores the 4-digit employeeId, not the document id. When that ID
 * matches more than one live employee (C4a) the name resolves as the Loans
 * page does — the last match — but `docId` is null so no member number prints.
 *
 * @param {Object} args
 * @param {Array<Object>} args.loans
 * @param {Array<{ id: string, employeeId: string, fullName: string }>} args.employees
 * @param {string} args.nameKey - a loanNameOptions key
 * @param {string} args.month - 'YYYY-MM'
 * @returns {{ rows: Array<Object>, total: number }}
 */
export const buildLoanRemittanceRows = ({ loans, employees, nameKey, month }) => {
  const byEmployeeId = new Map();
  (employees || []).forEach((e) => {
    const k = String(e.employeeId);
    byEmployeeId.set(k, [...(byEmployeeId.get(k) || []), e]);
  });

  const rows = (loans || [])
    .filter((loan) => normalizeLoanName(loan?.name) === nameKey)
    .map((loan) => ({ loan, deducted: round2(num(loan.paidByMonth?.[month])) }))
    .filter(({ deducted }) => deducted > 0)
    .map(({ loan, deducted }) => {
      const employeeId = String(loan.employeeId);
      const matches = byEmployeeId.get(employeeId) || [];
      const match = matches[matches.length - 1];
      return {
        loanId: loan.id,
        employeeId,
        docId: matches.length === 1 ? matches[0].id : null,
        name: match?.fullName || `EMP-${employeeId}`,
        monthly: num(loan.monthlyAmortization),
        deducted,
        remaining: remainingAtMonthEnd(loan, month),
      };
    })
    .sort(
      (a, b) =>
        a.employeeId.localeCompare(b.employeeId, undefined, { numeric: true }) ||
        String(a.loanId).localeCompare(String(b.loanId))
    );

  return { rows, total: round2(rows.reduce((sum, r) => sum + r.deducted, 0)) };
};
